import { useMutation } from '@tanstack/react-query'
import { Badge, Button, Flex, Text } from '@radix-ui/themes'
import { useMeterMakerCatalog } from '~/features/installations/useMeterMakers'
import { formatTimeAgo } from '~/utils/date'
import { useTRPC } from '~/utils/trpc'
import {
  suggestedCompatibilityColor,
  suggestedCompatibilityLabel,
  toSuggestedCompatibility,
  type SuggestionFields
} from '../labels'
import { useInvalidateMeterPhotos } from '../useInvalidateMeterPhotos'

const OUTCOME_NOTE = {
  unconfigured: 'Suggestions are off: ANTHROPIC_API_KEY is not set on the API.',
  'no-photos': 'Add a meter photo first.',
  'unreadable-photos': 'No photo could be read (too large or missing from storage).',
  refused: 'The model declined these photos.',
  malformed: 'The model answered in an unexpected shape.'
} as const

export const SuggestionPanel = ({
  installation,
  hasPhotos
}: {
  installation: SuggestionFields
  hasPhotos: boolean
}) => {
  const trpc = useTRPC()
  const invalidate = useInvalidateMeterPhotos()
  const { labelOf } = useMeterMakerCatalog()
  const suggest = useMutation(trpc.ops.compatibilityReview.suggest.mutationOptions({ onSettled: invalidate }))
  const compatibility = toSuggestedCompatibility(installation.suggested_compatibility)
  // Set only by a failure and cleared by the next success, so it is always the latest attempt.
  const failedAt = installation.suggestion_failed_at
  const outcome = suggest.data?.outcome
  const outcomeNote = outcome && outcome !== 'suggested' ? OUTCOME_NOTE[outcome] : null

  return (
    <Flex direction="column" gap="2" p="3" className="rounded-[var(--radius-3)] bg-[var(--gray-a2)]">
      <Flex align="center" justify="between" gap="2">
        <Text size="1" weight="medium" color="gray">
          AI suggestion
        </Text>
        <Button
          size="1"
          variant="ghost"
          disabled={!hasPhotos || suggest.isPending}
          onClick={() => suggest.mutate({ installationId: installation.installation_id })}
        >
          {suggest.isPending ? 'Asking…' : compatibility ? 'Suggest again' : 'Suggest now'}
        </Button>
      </Flex>

      {compatibility ? (
        <>
          <Flex align="center" gap="2" wrap="wrap">
            <Badge color={suggestedCompatibilityColor[compatibility]} variant="soft">
              {suggestedCompatibilityLabel[compatibility]}
            </Badge>
            <Text size="2">
              {installation.suggested_meter_maker ? labelOf(installation.suggested_meter_maker) : 'Unknown maker'}
              {installation.suggested_meter_model ? ` · ${installation.suggested_meter_model}` : ''}
            </Text>
            {installation.suggested_at ? (
              <Text size="1" color="gray">
                {formatTimeAgo(installation.suggested_at)}
              </Text>
            ) : null}
          </Flex>
          {installation.suggested_comment ? <Text size="2">{installation.suggested_comment}</Text> : null}
        </>
      ) : null}

      {failedAt ? (
        <Text size="2" color="amber">
          {compatibility
            ? `A newer attempt failed ${formatTimeAgo(failedAt)}, so this suggestion may not cover the latest photos.`
            : `The last attempt failed ${formatTimeAgo(failedAt)}.`}{' '}
          It runs again when a photo arrives.
        </Text>
      ) : null}

      {!compatibility && !failedAt ? (
        <Text size="2" color="gray">
          {hasPhotos ? 'Coming: suggestions run every ten minutes once photos settle.' : 'Waiting for photos.'}
        </Text>
      ) : null}

      {outcomeNote ? (
        <Text size="1" color="amber">
          {outcomeNote}
        </Text>
      ) : null}
      {suggest.error ? (
        <Text size="1" color="red">
          {suggest.error.message}
        </Text>
      ) : null}
    </Flex>
  )
}
